import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, Send, Download, CheckCircle2, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Modal "Générer un bon de mission".
 * - Charge les désignations du devis lié au chantier (sans prix)
 * - Permet de cocher les prestations
 * - Capte montant convenu, type prestation, dates, observations
 */
export function MissionDialog({
  open, onOpenChange, chantier, artisan, onCreated,
}) {
  const [loading, setLoading] = useState(false);
  const [designationsAvailable, setDesignationsAvailable] = useState([]);
  const [selectedIdxs, setSelectedIdxs] = useState(new Set());
  const [custom, setCustom] = useState('');
  const [montant, setMontant] = useState(artisan?.montant_prevu || '');
  const [typePrestation, setTypePrestation] = useState(
    artisan?.type_prestation === 'MO + fournitures'
      ? 'MO + fournitures'
      : "Main d'œuvre uniquement"
  );
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [delai, setDelai] = useState('');
  const [observations, setObservations] = useState('');
  const [emailDest, setEmailDest] = useState(artisan?.email || '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !chantier?.chantier_id) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/chantiers/${chantier.chantier_id}/devis-lignes`);
        setDesignationsAvailable(r.data.designations || []);
        setSelectedIdxs(new Set());
      } catch (e) {
        toast.error("Impossible de charger les prestations du devis");
      }
      setLoading(false);
    })();
  }, [open, chantier?.chantier_id]);

  const toggleIdx = (idx) => {
    setSelectedIdxs(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const handleGenerate = async (sendImmediately = false) => {
    if (!artisan?.sous_traitant_id) {
      toast.error("Sous-traitant inconnu"); return;
    }
    const montantNum = parseFloat(montant);
    if (isNaN(montantNum) || montantNum < 0) {
      toast.error("Montant convenu invalide"); return;
    }
    const selectedDesc = Array.from(selectedIdxs)
      .sort((a, b) => a - b)
      .map(i => designationsAvailable[i]?.description)
      .filter(Boolean);
    const customDesc = custom
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    const designations = [...selectedDesc, ...customDesc];
    if (designations.length === 0) {
      toast.error("Sélectionne au moins une prestation"); return;
    }

    setSubmitting(true);
    try {
      const payload = {
        sous_traitant_id: artisan.sous_traitant_id,
        affectation_id: artisan.affectation_id,
        montant_convenu: montantNum,
        type_prestation: typePrestation,
        designations,
        date_debut: dateDebut || null,
        date_fin: dateFin || null,
        delai_jours: delai ? parseInt(delai, 10) : null,
        observations: observations || null,
        email_destinataire: emailDest || null,
      };
      const r = await api.post(
        `/chantiers/${chantier.chantier_id}/missions/generate`,
        payload
      );
      toast.success("Bon de mission créé (brouillon)");

      if (sendImmediately && emailDest) {
        try {
          await api.post(`/missions/${r.data.mission_id}/send`,
                            { email_destinataire: emailDest });
          toast.success("Email envoyé au sous-traitant");
        } catch (e) {
          toast.error("Brouillon créé mais email non envoyé");
        }
      }

      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mission-dialog">
        <DialogHeader>
          <DialogTitle>
            Générer un bon de mission —{' '}
            <span className="text-blue-700">{artisan?.nom}</span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-1">
            Les prix du devis client ne sont jamais exposés au sous-traitant.
            Seules les désignations sélectionnées sont reprises, avec le
            montant convenu avec lui.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Prestations du devis */}
          <section>
            <Label className="text-sm font-semibold text-slate-900">
              Prestations issues du devis chantier
            </Label>
            {loading ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : designationsAvailable.length === 0 ? (
              <p className="text-sm text-slate-500 mt-2">
                Aucun devis lié à ce chantier. Ajoute les prestations manuellement ci-dessous.
              </p>
            ) : (
              <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {designationsAvailable.map((d) => (
                  <label
                    key={d.index}
                    className="flex items-start gap-2 text-sm text-slate-700 hover:bg-slate-50 p-2 rounded cursor-pointer"
                    data-testid={`mission-designation-${d.index}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={selectedIdxs.has(d.index)}
                      onChange={() => toggleIdx(d.index)}
                    />
                    <span className="flex-1 leading-relaxed">{d.description}</span>
                  </label>
                ))}
              </div>
            )}
          </section>

          <section>
            <Label className="text-sm font-semibold text-slate-900">
              Prestations libres (une par ligne)
            </Label>
            <Textarea
              className="mt-1 text-sm"
              rows={2}
              placeholder="Ajouter des prestations spécifiques si besoin"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              data-testid="mission-custom-designations"
            />
          </section>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Montant convenu (€ HT)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                placeholder="0.00"
                data-testid="mission-montant"
              />
            </div>
            <div>
              <Label className="text-sm">Type de prestation</Label>
              <select
                className="w-full h-10 border border-slate-200 rounded-md px-3 text-sm"
                value={typePrestation}
                onChange={(e) => setTypePrestation(e.target.value)}
                data-testid="mission-type-prestation"
              >
                <option value="Main d'œuvre uniquement">Main d'œuvre uniquement</option>
                <option value="MO + fournitures">MO + fournitures</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-sm">Date début</Label>
              <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                       data-testid="mission-date-debut" />
            </div>
            <div>
              <Label className="text-sm">Date fin</Label>
              <Input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)}
                       data-testid="mission-date-fin" />
            </div>
            <div>
              <Label className="text-sm">Délai (jours)</Label>
              <Input type="number" min="1" value={delai} onChange={(e) => setDelai(e.target.value)}
                       data-testid="mission-delai" />
            </div>
          </div>

          <div>
            <Label className="text-sm">Observations</Label>
            <Textarea rows={2} value={observations}
                        onChange={(e) => setObservations(e.target.value)}
                        placeholder="Précisions sur la mission, accès, horaires…"
                        data-testid="mission-observations" />
          </div>

          <div>
            <Label className="text-sm">Email destinataire</Label>
            <Input
              type="email"
              value={emailDest}
              onChange={(e) => setEmailDest(e.target.value)}
              placeholder="email@sous-traitant.fr"
              data-testid="mission-email"
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => handleGenerate(false)}
            disabled={submitting}
            data-testid="mission-save-draft"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <FileText className="w-4 h-4 mr-2" /> Enregistrer en brouillon
          </Button>
          <Button
            onClick={() => handleGenerate(true)}
            disabled={submitting || !emailDest}
            data-testid="mission-save-and-send"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            <Send className="w-4 h-4 mr-2" /> Générer et envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Liste des bons de mission existants sur un chantier.
 */
export function MissionsList({ chantierId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!chantierId) return;
    setLoading(true);
    try {
      const r = await api.get(`/chantiers/${chantierId}/missions`);
      setItems(r.data.items || []);
    } catch (e) {
      // silencieux
    }
    setLoading(false);
  }, [chantierId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const handleSend = async (mission_id) => {
    try {
      await api.post(`/missions/${mission_id}/send`, {});
      toast.success("Email envoyé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };
  const handleDelete = async (mission_id, status) => {
    if (status === 'signed') { toast.error("Un bon signé ne peut être supprimé"); return; }
    if (!window.confirm("Supprimer ce brouillon ?")) return;
    try {
      await api.delete(`/missions/${mission_id}`);
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const statusBadge = (st) => {
    const map = {
      draft: { label: 'Brouillon', cls: 'bg-slate-100 text-slate-700', icon: FileText },
      sent: { label: 'Envoyé', cls: 'bg-amber-100 text-amber-800', icon: Clock },
      signed: { label: 'Signé', cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
    };
    const m = map[st] || map.draft;
    const Icon = m.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${m.cls}`}>
        <Icon className="w-3 h-3" /> {m.label}
      </span>
    );
  };

  const money = (v) =>
    (typeof v === 'number'
      ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v)
      : '—');

  if (loading) return <div className="py-3 text-xs text-slate-500">Chargement…</div>;
  if (items.length === 0) return null;

  return (
    <div className="mt-3 space-y-2" data-testid="missions-list">
      {items.map((m) => (
        <div
          key={m.mission_id}
          className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg p-2.5"
          data-testid={`mission-row-${m.mission_id}`}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {statusBadge(m.status)}
              <span className="text-xs font-mono text-slate-500">{m.mission_id}</span>
              <span className="text-xs text-slate-600">·</span>
              <span className="text-xs font-medium text-slate-800">{m.sous_traitant?.nom}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5 truncate">
              {m.terms?.type_prestation} · {money(m.terms?.montant_convenu)}
              {m.sent_at && ` · Envoyé le ${new Date(m.sent_at).toLocaleDateString('fr-FR')}`}
              {m.signed_at && ` · Signé le ${new Date(m.signed_at).toLocaleDateString('fr-FR')} par ${m.signed_by_name || ''}`}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {(m.signed_pdf_url || m.draft_pdf_url) && (
              <a href={m.signed_pdf_url || m.draft_pdf_url} target="_blank" rel="noreferrer">
                <Button variant="ghost" size="sm" className="h-7 px-2" data-testid={`mission-download-${m.mission_id}`}>
                  <Download className="w-3.5 h-3.5" />
                </Button>
              </a>
            )}
            {m.status === 'draft' && (
              <Button
                variant="ghost" size="sm" className="h-7 px-2 text-blue-700"
                onClick={() => handleSend(m.mission_id)}
                data-testid={`mission-send-${m.mission_id}`}
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            )}
            {m.status !== 'signed' && (
              <Button
                variant="ghost" size="sm" className="h-7 px-2 text-red-600"
                onClick={() => handleDelete(m.mission_id, m.status)}
                data-testid={`mission-delete-${m.mission_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
